import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { RiErrorWarningFill, RiRefreshLine } from "@remixicon/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";

import { Restricted } from "@/app/components/Restricted";
import { Avatar } from "@/common/components/Avatar";
import { SimpleIconButton } from "@/common/components/CommonIconButtons";
import { CopyInput } from "@/common/components/CopyInput";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import { DocumentationRefButton } from "@/common/components/DocumentationReference";
import SimpleSelect from "@/common/components/SimpleSelect";
import TimestampCell from "@/common/components/TimestampCell";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { MemberActions } from "@/global-settings/components/MemberActions";
import {
  MemberRolePicker,
  roleOptions,
} from "@/global-settings/components/MemberRolePicker";
import { orgQueryKeys } from "@/global-settings/data/orgQueryKeys";
import { useInviteData } from "@/global-settings/data/useInviteData";
import {
  useCreateOrganizationDomainMutation,
  useDeleteOrganizationDomainMutation,
  useOrganizationDomains,
} from "@/global-settings/data/useOrganizationDomains";
import { useOrgMembers } from "@/global-settings/data/useOrgMembers";
import { usePatchOrgMutation } from "@/global-settings/data/usePatchOrgMutation";
import { useFlag } from "@reflag/react-sdk";
import { Member, Org } from "@reflag/shared/organizationAPI";
import { MemberRole } from "@reflag/shared/permissions";
import { slug } from "@reflag/shared/urls";

function RenewKey() {
  const toast = useToast();
  const errorToast = useErrorToast();
  const { org } = useEnvironment();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  const { mutate: renewKey, isPending: isRenewalLoading } = useMutation({
    mutationFn: () =>
      api.post<"/orgs/:orgId/invite/refresh">(`/orgs/${org.id}/invite/refresh`),

    retry: 0,

    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: orgQueryKeys.invite(org.id),
      });

      toast({
        title: "Invite key renewed",
        status: "success",
      });

      onClose();
    },

    onError: () => {
      errorToast({
        title: "Couldn't renew invite key",
      });
      onClose();
    },
  });

  return (
    <>
      <SimpleIconButton
        icon={<RiRefreshLine size={16} />}
        isLoading={isRenewalLoading}
        label="Generate new key"
        variant="outline"
        onClick={onOpen}
      />
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Generate new key</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to generate a new invite key?
              <HStack
                color={useColorModeValue("red.500", "red.400")}
                mt={3}
                spacing={1}
              >
                <RiErrorWarningFill size={16} />
                <Text fontWeight="medium">
                  The current invite link will be invalidated.
                </Text>
              </HStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={isRenewalLoading}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button isLoading={isRenewalLoading} onClick={() => renewKey()}>
                  Renew
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

function AutoJoinDomains() {
  const { org } = useEnvironment();
  const toast = useToast();
  const errorToast = useErrorToast();
  const { data, isLoading } = useOrganizationDomains(org.id);
  const createDomain = useCreateOrganizationDomainMutation(org.id);
  const deleteDomain = useDeleteOrganizationDomainMutation(org.id);
  const [addingDomain, setAddingDomain] = useState<string | undefined>();
  const [deletingDomainId, setDeletingDomainId] = useState<
    string | undefined
  >();

  const showMutationError = (
    error: { response?: { data?: { error?: { message?: string } } } },
    title: string,
  ) => {
    errorToast({
      title: error.response?.data?.error?.message ?? title,
    });
  };

  const candidateDomains = data?.candidateDomains ?? [];

  const handleAddDomain = (domain: string) => {
    setAddingDomain(domain);

    createDomain.mutate(
      { domain },
      {
        onSuccess: () => {
          toast({
            title: "Auto-join domain added",
            status: "success",
          });
        },
        onError: (error) => {
          showMutationError(error, "Failed to add auto-join domain");
        },
        onSettled: () => {
          setAddingDomain(undefined);
        },
      },
    );
  };

  const handleDeleteDomain = (domainId: string) => {
    setDeletingDomainId(domainId);

    deleteDomain.mutate(
      { domainId },
      {
        onSuccess: () => {
          toast({
            title: "Auto-join domain removed",
            status: "success",
          });
        },
        onError: (error) => {
          showMutationError(error, "Failed to remove auto-join domain");
        },
        onSettled: () => {
          setDeletingDomainId(undefined);
        },
      },
    );
  };

  return (
    <Flex direction="column" gap={3} w="full">
      <Flex direction="column">
        <FormLabel mb={0} pb={0}>
          Auto-join domains
        </FormLabel>
        <Text color="dimmed" fontSize="sm" mt={1}>
          Users that sign up with an email domain listed here will automatically
          join your organization. To add another auto-join domain, first invite
          a member who signs in with that domain. Once the user has signed up
          with the new email domain, you&apos;ll be able to add the domain as an
          auto-join domain here.
        </Text>
      </Flex>

      {isLoading ? <DelayedSpinner /> : null}

      {!isLoading && data?.domains.length ? (
        <Flex direction="column" gap={2}>
          {data.domains.map((item) => (
            <HStack key={item.id} spacing={3}>
              <Text fontSize="sm" fontWeight="semibold">
                @{item.domain}
              </Text>
              <Button
                isDisabled={deleteDomain.isPending}
                isLoading={
                  deleteDomain.isPending && deletingDomainId === item.id
                }
                size="xs"
                variant="ghostDimmed"
                onClick={() => handleDeleteDomain(item.id)}
              >
                Remove
              </Button>
            </HStack>
          ))}
        </Flex>
      ) : !isLoading ? (
        <Text color="dimmed" fontSize="sm">
          No auto-join domains yet.
        </Text>
      ) : null}

      {candidateDomains.length ? (
        <Flex direction="column" gap={2}>
          {candidateDomains.map((domain) => (
            <HStack key={domain} spacing={3}>
              <Text fontSize="sm" fontWeight="semibold">
                @{domain}
              </Text>
              <Button
                isDisabled={createDomain.isPending}
                isLoading={createDomain.isPending && addingDomain === domain}
                size="xs"
                variant="ghostDimmed"
                onClick={() => handleAddDomain(domain)}
              >
                Add auto-join
              </Button>
            </HStack>
          ))}
        </Flex>
      ) : null}
    </Flex>
  );
}

function DefaultMemberRolePicker() {
  const { org } = useEnvironment();
  const toast = useToast();
  const errorToast = useErrorToast();
  const patchOrgMutation = usePatchOrgMutation(org.id);
  const { isEnabled: enforceUserPermissions } = useFlag("user-permissions");
  const isStarterPlan = org.accessLevel === "starter";
  const effectiveDefaultMemberRole = isStarterPlan
    ? "admin"
    : org.defaultMemberRole;

  const handleRoleChange = (defaultMemberRole: MemberRole) => {
    if (defaultMemberRole === org.defaultMemberRole) {
      return;
    }

    patchOrgMutation.mutate(
      { defaultMemberRole },
      {
        onSuccess: () => {
          toast({
            title: "Default member role updated",
            status: "success",
          });
        },
        onError: () => {
          errorToast({
            title: "Failed to update default member role",
          });
        },
      },
    );
  };

  if (!enforceUserPermissions) {
    return null;
  }

  return (
    <Flex direction="column" gap={2} w="full">
      <Flex direction="column">
        <FormLabel>Default member role</FormLabel>
        <Text color="dimmed" fontSize="sm">
          {isStarterPlan
            ? "Starter plan organizations assign new joiners the Admin role by default."
            : "New joiners will be assigned this role by default."}
        </Text>
      </Flex>
      <Restricted alertStyle="tooltip" role="admin">
        <SimpleSelect<MemberRole>
          _menuList={{ maxW: "lg" }}
          isDisabled={patchOrgMutation.isPending || isStarterPlan}
          maxW={80}
          minW={60}
          options={roleOptions}
          size="sm"
          value={effectiveDefaultMemberRole}
          onChange={handleRoleChange}
        />
      </Restricted>
    </Flex>
  );
}

function InviteNewMembersNote({ org }: { org: Org }) {
  return (
    <Restricted alertStyle="none" displayContents="hidden" role="admin">
      <Flex direction="column">
        <FormLabel>Invite new members</FormLabel>
        <Text color="gray.500" fontSize="sm">
          Share the link below to invite new members to your organization:
        </Text>
        <InviteKeyDisplay org={org} />
      </Flex>
    </Restricted>
  );
}

export default function Team() {
  const { org } = useEnvironment();

  return (
    <Flex direction="column" gap={4} maxW="wideForm">
      <Restricted alertStyle="callout" role="admin">
        <Flex direction="column" gap={4}>
          <InviteNewMembersNote org={org} />
          <AutoJoinDomains />
          <DefaultMemberRolePicker />
          <Divider />

          <Flex direction="column">
            <FormLabel pb={1}>Members</FormLabel>
            <Flex align="center">
              <Text color="gray.500" fontSize="sm">
                Manage your team members and their roles.
              </Text>
              <DocumentationRefButton section="product-handbook/team-permissions" />
            </Flex>
            <MembersTable />
          </Flex>
        </Flex>
      </Restricted>
    </Flex>
  );
}

const columnHelper = createColumnHelper<Member>();

const MemberColumnConfig = [
  columnHelper.accessor("name", {
    header: "Name",
    size: 100,
    cell: (cell) => {
      const value = cell.getValue();
      return (
        <Grid
          columnGap={3}
          fontSize="sm"
          rowGap={0.5}
          templateColumns="min-content 1fr"
        >
          <GridItem rowSpan={2}>
            <Avatar
              display="flex"
              name={value}
              size="md"
              src={cell.row.original.avatarUrl ?? undefined}
            />
          </GridItem>
          <GridItem>
            <Text>{value}</Text>
          </GridItem>
          <GridItem>
            <Text color="dimmed">{cell.row.original.email}</Text>
          </GridItem>
        </Grid>
      );
    },
  }),
  columnHelper.accessor("joinedAt", {
    header: "Joined at",
    cell: (cell) => (
      <TimestampCell fontSize="sm" value={cell.getValue()}></TimestampCell>
    ),
  }),
  columnHelper.display({
    id: "role",
    header: () => <Text>Role</Text>,
    cell: (cell) => <MemberRolePicker member={cell.row.original} />,
  }),
  columnHelper.display({
    id: "actions",
    header: () => <Text></Text>,
    cell: (cell) => <MemberActions member={cell.row.original} />,
  }),
];

function MembersTable() {
  const { org } = useEnvironment();

  // team members
  const orgId = org.id;
  const { data = [], isLoading } = useOrgMembers(orgId);

  const tableInstance = useReactTable({
    columns: MemberColumnConfig,
    data: data,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return null;
  }

  return (
    <Restricted
      alertStyle="callout"
      displayContents="normal"
      flagKey="user-permissions"
    >
      <Table w="full">
        <Thead>
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {
            // Loop over the table rows
            tableInstance.getRowModel().rows.map((row) => {
              return (
                <Tr key={row.id}>
                  {row.getAllCells().map((cell) => {
                    return (
                      <Td
                        key={cell.id}
                        width={`${cell.column.columnDef.size}%`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })
          }
        </Tbody>
      </Table>
    </Restricted>
  );
}

export function InviteKeyDisplay({
  org,
  editable = true,
}: {
  org: Pick<Org, "id" | "name">;
  editable?: boolean;
}) {
  const { data: inviteData, isLoading } = useInviteData(org.id);

  const url = useMemo(() => {
    const orgSlug = slug({ id: org.id, name: org.name });
    return inviteData?.inviteKey
      ? new URL(
          `/orgs/${orgSlug}/invite/${inviteData.inviteKey}`,
          window.location.toString(),
        ).toString()
      : "";
  }, [org, inviteData]);

  return (
    <HStack>
      {isLoading ? (
        <DelayedSpinner />
      ) : (
        <>
          <CopyInput flexGrow={1} value={url} />
          {editable && <RenewKey />}
        </>
      )}
    </HStack>
  );
}
